import { KeyOutlined, LoadingOutlined } from '@ant-design/icons'
import { Button, Col, Row } from 'antd'
import classNames from 'classnames'
import * as R from 'ramda'
import React from 'react'
import { connect } from 'react-redux'
import { firestoreConnect } from 'react-redux-firebase'
import { compose, withHandlers, withState, withStateHandlers } from 'recompose'

import { dataFromOrdered, fsCollections, withoutUndefined } from '../../helpers/firestore'
import { printFlightNumber } from '../../helpers/flights'
import { resultKey } from '../../helpers/results'
import prepareFlightResults from '../tournament/prepareFlightResults'

import { Flight } from '../../schemas/Flight'
import { TournamentPlayer } from '../../schemas/Player'
import { Playground } from '../../schemas/Playground'
import { CombinedResults, FlightResults, PlayerRoundResult } from '../../schemas/Results'
import { Tournament } from '../../schemas/Tournament'
import FinishModal from './FinishModal'
import PinEntryModal from './PinEntryModal'
import SaveErrorModal from './SaveErrorModal'
import StartModal from './StartModal'

import './MobileFlightResults.css'

type MobileFlightResultsProps = {
  tournament: Tournament
  flight: Flight
  flightResults: FlightResults
  tournamentResults: PlayerRoundResult[]
  tournamentPlayersMap: { [playerId: string]: TournamentPlayer }
  tournamentCombinedResultsMap: { [id: string]: CombinedResults }
  playground: Playground
  unconfirmedResult: number
  savingResult: boolean
  saveErrorModalVisible: boolean
  editLastCommitted: boolean
  pinEntryModalVisible: boolean
  startModalVisible: boolean
  finishModalEnabled: boolean
  setUnconfirmedResult(result: number | undefined): void
  setEditLastCommitted(): void
  confirmResult(
    playersByIndex: TournamentPlayer[],
    activePlayerKey: number,
    hole: number,
  ): Promise<void>
  showPinEntryModal(): void
  hidePinEntryModal(): void
  hideSaveErrorModal(): void
  hideStartModal(): void
  enableFinishModal(): void
  disableFinishModal(): void
}

type MobileFlightResultsState = {
  totalResults: number
}

class MobileFlightResults extends React.Component<
  MobileFlightResultsProps,
  MobileFlightResultsState
> {
  /**
   * One issue with transactional updates is that the transaction as a whole is acknowledged independently of (and concurrently with)
   * its effects. That means we can (and on a slow connection often will) receive the write of new result before or after the transaction
   * as a whole is acknowledged.
   *
   * Ideally, we'd like to unlock the UI and move to a new result cell atomically after the transaction is acked. However, if the write
   * is received before transaction ack, the active cell will shift to the next one before the UI is unlocked & other cleanup state
   * transitions.
   *
   * The most annoing effect of all is that result for the last confirmed hole shows up in the next hole once the current hole marker
   * moves and it stays there until transaction ack is received. To compensate for that, we detect new result has been received from firestore
   * in this lifecycle callback and when it arrives, we clear the current hole result.
   */
  componentDidUpdate() {
    const { flightResults, setUnconfirmedResult, savingResult } = this.props
    const prevTotalResults = this.state && this.state.totalResults

    const allPlayerResults = R.chain(
      (playerRes) => playerRes.results,
      R.values(flightResults || {}),
    )

    const totalResults = allPlayerResults.filter(R.identity).length

    if (totalResults !== prevTotalResults) {
      if (savingResult) {
        setUnconfirmedResult(undefined) // <- new result arrived, clear the current hole result early before transaction ack
      }
      this.setState(() => ({
        totalResults,
      }))
    }
  }

  render() {
    const {
      tournament,
      flight,
      flightResults,
      tournamentCombinedResultsMap,
      playground,
      unconfirmedResult,
      savingResult,
      saveErrorModalVisible,
      setUnconfirmedResult,
      editLastCommitted,
      pinEntryModalVisible,
      setEditLastCommitted,
      confirmResult,
      showPinEntryModal,
      hidePinEntryModal,
      hideSaveErrorModal,
      startModalVisible,
      hideStartModal,
      finishModalEnabled,
      disableFinishModal,
    } = this.props

    // TODO get logic out to handlers
    const results = R.values(flightResults)
    const playersShown = Math.max(3, results.length) // always show at least 3 players in the form

    const playersByIndex = R.values(flightResults).reduce((acc, res) => {
      const copy: FIXME[] = acc.slice()
      copy[res.orderInRound] = res

      return copy
    }, [])
    const withPlayerByIndex = (index) => (handler) => handler(playersByIndex[index], index)
    const withPotentialPlayers = (handler) =>
      R.range(0, playersShown).map((idx) => withPlayerByIndex(idx)(handler))

    const getResult = (playerKey, hole) => {
      const player = playersByIndex[playerKey]
      const res = player && player.results[hole]

      return res !== 0 && res
    }

    const getPar = (playerId) => {
      return R.pathOr(0, [playerId, 'withFinal', 'parDiff'], tournamentCombinedResultsMap)
    }

    const getSum = (playerKey) => {
      const player = playersByIndex[playerKey]

      return player && R.sum(player.results)
    }

    const holesXPlayers = R.xprod(
      R.range(0, playground.holes),
      Object.keys(playersByIndex).map(Number),
    )

    const activeRecord = editLastCommitted
      ? R.findLast(([hole, playerKey]) => !!getResult(playerKey, hole), holesXPlayers)
      : R.find(([hole, playerKey]) => !getResult(playerKey, hole), holesXPlayers)

    const activeHole = activeRecord && R.head(activeRecord)
    const activePlayerKey = activeRecord && R.last(activeRecord)

    const isActivePlayer = (playerKey) => activePlayerKey === playerKey
    const isActiveHole = (hole) => activeHole === hole

    const finishModalVisible = finishModalEnabled && R.isNil(activeRecord)

    const showResult = (playerKey, hole) =>
      isActivePlayer(playerKey) && isActiveHole(hole) && unconfirmedResult
        ? unconfirmedResult
        : getResult(playerKey, hole)

    const handleUndoClick = () => {
      if (unconfirmedResult) {
        setUnconfirmedResult(undefined)
      } else if (!editLastCommitted) {
        showPinEntryModal()
      }
    }

    const saveCurrentResult = () =>
      confirmResult(playersByIndex, activePlayerKey as FIXME, activeHole as FIXME)

    const handleOkClick = () => !R.isNil(activeHole) && saveCurrentResult()

    const undoEnabled = unconfirmedResult || !editLastCommitted

    const holeRows = R.range(0, playground.holes).map((hole) => (
      <tr key={hole}>
        <td
          className={classNames('hole-header', {
            'active-hole': isActiveHole(hole),
          })}>
          {hole + 1}.
        </td>
        {withPotentialPlayers((player, playerKey) => (
          <td
            key={playerKey || 'undefinedPlayer'}
            className={classNames('player-col', 'result-cell', {
              'active-player': isActivePlayer(playerKey),
              'active-hole': isActiveHole(hole),
              'old-result': editLastCommitted && !unconfirmedResult,
            })}>
            {showResult(playerKey, hole)}
          </td>
        ))}
      </tr>
    ))

    const statsRows = (
      <React.Fragment>
        <tr className="stats-row__sum">
          <td className="stat-header">SUM</td>
          {withPotentialPlayers((player, playerKey) => (
            <td className="stat" key={playerKey}>
              {player && getSum(playerKey)}
            </td>
          ))}
        </tr>
        <tr className="stats_row__par">
          <td className="stat-header">PAR</td>
          {withPotentialPlayers((player, playerKey) => (
            <td className="stat" key={playerKey}>
              {player && getPar(player.playerId)}
            </td>
          ))}
        </tr>
      </React.Fragment>
    )

    const makeStrokeHandler = (strokeCount) => () => setUnconfirmedResult(strokeCount)

    const strokeButtons = R.range(1, 8).map((strokes) => (
      <tr key={strokes}>
        <td>
          <Button
            className="control__button stroke__button"
            disabled={activePlayerKey === undefined || savingResult}
            onClick={makeStrokeHandler(strokes)}>
            {strokes}
          </Button>
        </td>
      </tr>
    ))

    return (
      <React.Fragment>
        {pinEntryModalVisible && (
          <PinEntryModal
            hide={hidePinEntryModal}
            hashedPin={tournament.hashedPin}
            onSuccessfulEntry={setEditLastCommitted}
          />
        )}
        {!finishModalVisible && startModalVisible && (
          <StartModal
            round={flight.round}
            flightNumber={flight.number}
            startTime={flight.startTime}
            playerNames={withPotentialPlayers((player) => player && player.name)}
            hide={hideStartModal}
          />
        )}
        {saveErrorModalVisible && (
          <SaveErrorModal retry={saveCurrentResult} hide={hideSaveErrorModal} />
        )}
        {finishModalVisible && (
          <FinishModal
            round={flight.round}
            flightNumber={flight.number}
            playersWithScore={withPotentialPlayers(
              (player) =>
                player && {
                  name: player.name,
                  totalScore: R.sum(player.results),
                },
            ).filter(Boolean)}
            hide={disableFinishModal}
          />
        )}
        <div className="result-container">
          <Row className="grab-vertical-space">
            <Col span={20} className="grab-vertical-space">
              <div className="results-table__wrapper">
                <table className="result-table">
                  <thead>
                    <tr>
                      <th className="flight-number">{printFlightNumber(flight)}</th>
                      {withPotentialPlayers((player, playerKey) => (
                        <th
                          key={playerKey}
                          className={classNames('player-header', 'player-col', {
                            'active-player': isActivePlayer(playerKey),
                          })}>
                          {player && player.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {holeRows}
                    {statsRows}
                  </tbody>
                </table>
              </div>
            </Col>
            <Col span={4} className="grab-vertical-space">
              <table className="controls">
                <tbody>
                  <tr>
                    <td>
                      <Button
                        className="control__button"
                        style={{ fontSize: '1rem', fontWeight: 800 }}
                        type="primary"
                        disabled={!unconfirmedResult || savingResult}
                        onClick={handleOkClick}>
                        {savingResult ? <LoadingOutlined /> : 'OK'}
                      </Button>
                    </td>
                  </tr>
                  {strokeButtons}
                  <tr>
                    <td>
                      <Button
                        className="control__button"
                        style={{ fontSize: '1rem', fontWeight: 800 }}
                        disabled={!undoEnabled}
                        onClick={handleUndoClick}>
                        <KeyOutlined />
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        </div>
      </React.Fragment>
    )
  }
}

export default compose<MobileFlightResultsProps, FIXME>(
  prepareFlightResults,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.tournamentCombinedResults(tenant, tournamentId) },
  ]),
  connect(
    (
      { firestore, firebase: { profile } }: FIXME,
      { tenant, tournamentId, flightResults }: FIXME,
    ) => {
      const tournamentCombinedResults =
        firestore.ordered[fsCollections.tournamentCombinedResults(tenant, tournamentId)]

      const resultKeys = Object.values(flightResults || {}).map(
        (flightResult: FIXME) => flightResult.playerId,
      )

      return {
        tournamentCombinedResultsMap: R.isNil(tournamentCombinedResults)
          ? undefined
          : dataFromOrdered(
              tournamentCombinedResults.filter((result) => resultKeys.includes(result.id)),
            ),
      }
    },
  ),
  withState('unconfirmedResult', 'setUnconfirmedResult', undefined),
  withState('savingResult', 'setSavingResult', false),
  withState('saveError', 'setSaveError', undefined),
  withStateHandlers(
    { editLastCommitted: false },
    {
      setEditLastCommitted: () => () => ({ editLastCommitted: true }),
      unsetEditLastCommitted: () => () => ({ editLastCommitted: false }),
    },
  ),
  withStateHandlers(
    { pinEntryModalVisible: false },
    {
      showPinEntryModal: () => () => ({ pinEntryModalVisible: true }),
      hidePinEntryModal: () => () => ({ pinEntryModalVisible: false }),
    },
  ),
  withStateHandlers(
    { saveErrorModalVisible: false },
    {
      showSaveErrorModal: () => () => ({ saveErrorModalVisible: true }),
      hideSaveErrorModal: () => () => ({ saveErrorModalVisible: false }),
    },
  ),
  withStateHandlers(
    { startModalVisible: true },
    { hideStartModal: () => () => ({ startModalVisible: false }) },
  ),
  withStateHandlers(
    { finishModalEnabled: true },
    {
      enableFinishModal: () => () => ({ finishModalEnabled: true }),
      disableFinishModal: () => () => ({ finishModalEnabled: false }),
    },
  ),
  withHandlers({
    saveResults: ({ firebase, tenant, tournamentId }) => (round, isFinal, playerId, results) => {
      const firestore = firebase.firestore()

      return firestore.runTransaction((transaction) => {
        const collection = firestore.collection(
          fsCollections.tournamentResults(tenant, tournamentId),
        )
        const id = resultKey(playerId, round)

        transaction.set(
          collection.doc(id),
          withoutUndefined({
            id,
            round,
            playerId,
            results,
            isFinal: !R.isNil(isFinal) && isFinal,
          }),
        )

        // transaction callback must return a promise, since we're not reading any
        // input data for the transaction, we can just return an empty resolved one
        return Promise.resolve()
      })
    },
  }),
  withHandlers({
    confirmResult: ({
      playground,
      flight,
      unconfirmedResult,
      saveResults,
      setSaveError,
      setSavingResult,
      setUnconfirmedResult,
      unsetEditLastCommitted,
      showSaveErrorModal,
      enableFinishModal,
    }) => (playersByIndex, activePlayerKey, currentHoleKey) => {
      const player = playersByIndex[activePlayerKey]
      const newResults = R.set(R.lensIndex(currentHoleKey), unconfirmedResult, player.results)

      const withoutCurrentHole =
        activePlayerKey === playersByIndex.length - 1 && currentHoleKey === playground.holes - 1

      setSavingResult(true)

      return saveResults(flight.round, flight.isFinal, player.playerId, newResults)
        .then(() => {
          if (withoutCurrentHole) {
            const otherPlayers = playersByIndex.slice(0, activePlayerKey)

            // TODO should happen in same transaction as saving last result for flight
            return Promise.all(
              otherPlayers.map((otherPlayer) =>
                saveResults(
                  flight.round,
                  flight.isFinal,
                  otherPlayer.playerId,
                  otherPlayer.results,
                  undefined,
                ),
              ),
            )
          }

          return Promise.resolve()
        })
        .then(() => {
          setSavingResult(false)
          setSaveError(undefined)
          setUnconfirmedResult(undefined)
          unsetEditLastCommitted()
          enableFinishModal()
        })
        .catch((e) => {
          setSaveError(e)
          setSavingResult(false)
          showSaveErrorModal()
          throw e
        })
    },
  }),
)(MobileFlightResults)



// WEBPACK FOOTER //
// ./src/components/mobile/MobileFlightResults.tsx